// ════════════════════════════════════════════════════════════════════════════
// FESTIVAL THEME MANAGER — admin sets the active festival overlay; storefront's
// <FestivalThemeApplier /> reads `homepage_festival_theme` and re-skins via
// CSS custom properties.
// ════════════════════════════════════════════════════════════════════════════
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Power, Save } from 'lucide-react';
import { useEffect, useState } from 'react';

import { cms } from '@terra/contracts';
import { Button, Spinner } from '@terra/ui';

import { LivePreviewPanel } from '../components/LivePreviewPanel';
import { api } from '../lib/api';
import { usePreviewStore } from '../stores/previewStore';

// ── Shared types and constants imported from @terra/contracts ───────────────────

type FestivalPresetId = cms.FestivalPresetId;
type FestivalPalette = cms.FestivalPalette;
type FestivalAnnouncement = cms.FestivalAnnouncement;
type FestivalPreset = cms.FestivalPreset;
type FestivalThemeSetting = cms.FestivalThemeSetting;

const { FESTIVAL_THEMES, PRESET_ORDER } = cms;

// ── Small helpers ───────────────────────────────────────────────────────────────

function ColorInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="space-y-1">
      <label className="text-ink-muted ml-1 text-[10px] font-bold uppercase tracking-wider">
        {label}
      </label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="border-border/60 h-9 w-9 shrink-0 cursor-pointer rounded-lg border bg-transparent p-0"
        />
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="bg-surface border-border/60 h-9 flex-1 rounded-lg border px-3 font-mono text-[12px] outline-none"
        />
      </div>
    </div>
  );
}

function PresetSwatchCard({
  preset,
  isActive,
  onSelect,
}: {
  preset: FestivalPreset;
  isActive: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`group relative flex flex-col gap-3 rounded-xl border-2 p-4 text-left transition-all ${
        isActive
          ? 'border-brand-primary shadow-brand-primary/20 shadow-lg'
          : 'border-border/60 hover:border-brand-primary/50 hover:shadow-md'
      }`}
    >
      <div className="flex items-center justify-between">
        <span className="text-2xl">{preset.emoji}</span>
        {isActive && (
          <span className="bg-brand-primary grid h-6 w-6 place-items-center rounded-full text-white">
            <Check className="h-3.5 w-3.5" strokeWidth={3} />
          </span>
        )}
      </div>
      <div className="space-y-1">
        <p className="text-ink text-[13px] font-bold">{preset.name}</p>
        <p className="text-ink-muted line-clamp-2 text-[11px]">{preset.description}</p>
      </div>
      <div className="flex gap-1">
        <span
          className="h-6 w-6 rounded-md border border-black/5 shadow-sm"
          style={{ background: preset.palette.primary }}
          title={`Primary ${preset.palette.primary}`}
        />
        <span
          className="h-6 w-6 rounded-md border border-black/5 shadow-sm"
          style={{ background: preset.palette.gold }}
          title={`Gold ${preset.palette.gold}`}
        />
        <span
          className="h-6 w-6 rounded-md border border-black/5 shadow-sm"
          style={{ background: preset.palette.topbarBg }}
          title={`Top bar ${preset.palette.topbarBg}`}
        />
      </div>
      {preset.announcement && (
        <div
          className="truncate rounded-md px-2 py-1.5 text-[10px] font-bold"
          style={{ background: preset.announcement.bg, color: preset.announcement.fg }}
          title={preset.announcement.text}
        >
          {preset.announcement.text}
        </div>
      )}
    </button>
  );
}

// ── Page ────────────────────────────────────────────────────────────────────────

const EMPTY_CUSTOM_PALETTE: FestivalPalette = {
  primary: '#d63232',
  hover: '#b92d2d',
  gold: '#d9a93a',
  goldHover: '#b98b2c',
  topbarBg: '#2b2b2b',
  topbarText: '#ffffff',
};

export function FestivalThemePage() {
  const qc = useQueryClient();
  const { data: setting, isLoading } = useQuery({
    queryKey: ['website-setting', 'homepage_festival_theme'],
    queryFn: () => api.cms.getSetting('homepage_festival_theme'),
  });

  const [presetId, setPresetId] = useState<FestivalPresetId>('none');
  const [active, setActive] = useState<boolean>(false);
  const [customPalette, setCustomPalette] = useState<FestivalPalette>(EMPTY_CUSTOM_PALETTE);
  const [customAnnText, setCustomAnnText] = useState('');
  const [customAnnBg, setCustomAnnBg] = useState('#2b2b2b');
  const [customAnnFg, setCustomAnnFg] = useState('#ffffff');
  const [enableCustomAnn, setEnableCustomAnn] = useState(false);
  // Schedule — both empty means "always on whenever `active` is true".
  // <input type="datetime-local"> values, e.g. '2026-10-25T18:00'.
  const [scheduleStartsAt, setScheduleStartsAt] = useState('');
  const [scheduleEndsAt, setScheduleEndsAt] = useState('');
  const [inited, setInited] = useState(false);

  // ISO ↔ datetime-local helpers (storefront stores ISO; <input> wants 16 chars).
  const isoToLocal = (iso: string | null | undefined) => (iso ? iso.slice(0, 16) : '');
  const localToIso = (v: string): string | null => {
    if (!v) return null;
    const d = new Date(v);
    return Number.isFinite(d.getTime()) ? d.toISOString() : null;
  };

  useEffect(() => {
    if (!setting || inited) return;
    const v = (setting.value as FestivalThemeSetting | null) ?? null;
    if (v) {
      setPresetId(v.presetId ?? 'none');
      setActive(Boolean(v.active));
      if (v.customPalette) setCustomPalette(v.customPalette);
      if (v.customAnnouncement) {
        setEnableCustomAnn(true);
        setCustomAnnText(v.customAnnouncement.text);
        setCustomAnnBg(v.customAnnouncement.bg);
        setCustomAnnFg(v.customAnnouncement.fg);
      }
      if (v.schedule) {
        setScheduleStartsAt(isoToLocal(v.schedule.startsAt));
        setScheduleEndsAt(isoToLocal(v.schedule.endsAt));
      }
    }
    setInited(true);
  }, [setting, inited]);

  const upsert = useMutation({
    mutationFn: () => {
      const startIso = localToIso(scheduleStartsAt);
      const endIso = localToIso(scheduleEndsAt);
      const payload: FestivalThemeSetting = {
        active,
        presetId,
        ...(presetId === 'custom'
          ? {
              customPalette,
              customAnnouncement: enableCustomAnn
                ? { text: customAnnText, bg: customAnnBg, fg: customAnnFg }
                : null,
            }
          : {}),
        // Only include schedule when at least one bound is set; null means no window.
        schedule: startIso || endIso ? { startsAt: startIso, endsAt: endIso } : null,
      };
      return api.cms.upsertSetting('homepage_festival_theme', payload);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['website-setting', 'homepage_festival_theme'] });
      void qc.invalidateQueries({
        queryKey: ['website-public-setting', 'homepage_festival_theme'],
      });
      // Bump LivePreviewPanel so the iframe reloads instantly with the new theme.
      usePreviewStore.getState().bump();
    },
  });

  if (isLoading) {
    return (
      <div className="grid min-h-[300px] place-items-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="grid gap-6 pb-8 xl:grid-cols-[1fr_minmax(380px,460px)]">
      {/* LEFT — editor */}
      <div className="space-y-6">
        <div className="mb-2 flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
          <div>
            <h1 className="text-ink text-[22px] font-extrabold tracking-tight">
              Festival Theme Manager
            </h1>
            <p className="text-ink-muted mt-1 text-[13px] font-medium">
              Re-skin the entire storefront for festivals. Choose a preset or build a custom
              palette; then toggle the theme live with the master switch.
            </p>
          </div>
        </div>

        {/* Master switch */}
        <div className="border-border/60 bg-surface rounded-2xl border shadow-sm">
          <div className="flex items-center justify-between gap-4 p-5">
            <div className="flex items-center gap-3">
              <span
                className={`grid h-10 w-10 place-items-center rounded-xl ${active ? 'bg-emerald-100 text-emerald-700' : 'bg-surface-subtle text-ink-muted'}`}
              >
                <Power className="h-5 w-5" />
              </span>
              <div>
                <p className="text-ink text-[14px] font-bold">
                  Festival theme is currently {active ? 'ACTIVE' : 'OFF'}
                </p>
                <p className="text-ink-muted text-[12px]">
                  When OFF the storefront uses its default palette regardless of selected preset.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setActive(!active)}
              className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${active ? 'bg-emerald-500' : 'bg-surface-subtle border-border/60 border'}`}
              aria-label="Toggle festival theme"
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform ${active ? 'translate-x-6' : 'translate-x-1'}`}
              />
            </button>
          </div>
        </div>

        {/* Optional schedule window — outside this window the theme is suppressed
          even when ACTIVE. Leave both empty for "always on whenever ACTIVE". */}
        <div className="border-border/60 bg-surface rounded-2xl border shadow-sm">
          <div className="border-border/60 border-b px-5 py-4">
            <h2 className="text-ink text-[15px] font-bold">Schedule (optional)</h2>
          </div>
          <div className="p-5">
            <p className="text-ink-muted mb-4 text-[12px] font-medium">
              Pre-stage seasonal themes — set a start and / or end date and the storefront will only
              apply this theme inside the window. Both fields empty = no time restriction.
            </p>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="text-ink-muted mb-1 block text-[10px] font-bold uppercase tracking-wider">
                  Starts At
                </label>
                <input
                  type="datetime-local"
                  value={scheduleStartsAt}
                  onChange={(e) => setScheduleStartsAt(e.target.value)}
                  className="bg-surface border-border/60 focus:border-brand-primary/50 h-10 w-full rounded-lg border px-3 text-[13px] font-medium outline-none transition-colors"
                />
              </div>
              <div>
                <label className="text-ink-muted mb-1 block text-[10px] font-bold uppercase tracking-wider">
                  Ends At
                </label>
                <input
                  type="datetime-local"
                  value={scheduleEndsAt}
                  onChange={(e) => setScheduleEndsAt(e.target.value)}
                  className="bg-surface border-border/60 focus:border-brand-primary/50 h-10 w-full rounded-lg border px-3 text-[13px] font-medium outline-none transition-colors"
                />
              </div>
            </div>
            {(scheduleStartsAt || scheduleEndsAt) && (
              <button
                type="button"
                onClick={() => {
                  setScheduleStartsAt('');
                  setScheduleEndsAt('');
                }}
                className="text-brand-primary mt-3 text-[12px] font-bold hover:underline"
              >
                Clear schedule (always on)
              </button>
            )}
          </div>
        </div>

        {/* Preset grid */}
        <div className="border-border/60 bg-surface rounded-2xl border shadow-sm">
          <div className="border-border/60 border-b px-5 py-4">
            <h2 className="text-ink text-[15px] font-bold">Choose Preset</h2>
          </div>
          <div className="p-5">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {PRESET_ORDER.map((id) => {
                if (id === 'custom') {
                  return (
                    <button
                      key="custom"
                      type="button"
                      onClick={() => setPresetId('custom')}
                      className={`group relative flex flex-col gap-3 rounded-xl border-2 p-4 text-left transition-all ${
                        presetId === 'custom'
                          ? 'border-brand-primary shadow-brand-primary/20 shadow-lg'
                          : 'border-border/60 hover:border-brand-primary/50 hover:shadow-md'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-2xl">🎨</span>
                        {presetId === 'custom' && (
                          <span className="bg-brand-primary grid h-6 w-6 place-items-center rounded-full text-white">
                            <Check className="h-3.5 w-3.5" strokeWidth={3} />
                          </span>
                        )}
                      </div>
                      <div className="space-y-1">
                        <p className="text-ink text-[13px] font-bold">Custom Theme</p>
                        <p className="text-ink-muted text-[11px]">
                          Pick your own colors + announcement strip below.
                        </p>
                      </div>
                    </button>
                  );
                }
                const preset = FESTIVAL_THEMES[id];
                return (
                  <PresetSwatchCard
                    key={id}
                    preset={preset}
                    isActive={presetId === id}
                    onSelect={() => setPresetId(id)}
                  />
                );
              })}
            </div>
          </div>
        </div>

        {/* Custom palette editor (visible only when 'custom' is the selected preset) */}
        {presetId === 'custom' && (
          <div className="border-border/60 bg-surface rounded-2xl border shadow-sm">
            <div className="border-border/60 border-b px-5 py-4">
              <h2 className="text-ink text-[15px] font-bold">Custom Palette</h2>
            </div>
            <div className="space-y-5 p-5">
              <div className="grid gap-4 md:grid-cols-3">
                <ColorInput
                  label="Primary"
                  value={customPalette.primary}
                  onChange={(v) => setCustomPalette({ ...customPalette, primary: v })}
                />
                <ColorInput
                  label="Primary hover"
                  value={customPalette.hover}
                  onChange={(v) => setCustomPalette({ ...customPalette, hover: v })}
                />
                <ColorInput
                  label="Gold accent"
                  value={customPalette.gold}
                  onChange={(v) => setCustomPalette({ ...customPalette, gold: v })}
                />
                <ColorInput
                  label="Gold hover"
                  value={customPalette.goldHover ?? '#b98b2c'}
                  onChange={(v) => setCustomPalette({ ...customPalette, goldHover: v })}
                />
                <ColorInput
                  label="Top bar background"
                  value={customPalette.topbarBg}
                  onChange={(v) => setCustomPalette({ ...customPalette, topbarBg: v })}
                />
                <ColorInput
                  label="Top bar text"
                  value={customPalette.topbarText}
                  onChange={(v) => setCustomPalette({ ...customPalette, topbarText: v })}
                />
              </div>

              <div className="border-border/60 space-y-3 rounded-xl border p-4">
                <label className="group flex cursor-pointer items-center justify-between">
                  <span className="text-ink text-[13px] font-bold">Show announcement strip</span>
                  <button
                    type="button"
                    onClick={() => setEnableCustomAnn(!enableCustomAnn)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${enableCustomAnn ? 'bg-brand-primary' : 'bg-surface-subtle border-border/60 border'}`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${enableCustomAnn ? 'translate-x-6' : 'translate-x-1'}`}
                    />
                  </button>
                </label>
                {enableCustomAnn && (
                  <>
                    <div className="space-y-1">
                      <label className="text-ink-muted ml-1 text-[10px] font-bold uppercase tracking-wider">
                        Announcement text
                      </label>
                      <input
                        value={customAnnText}
                        onChange={(e) => setCustomAnnText(e.target.value)}
                        placeholder="🎉 Mega Sale — Up to 50% off"
                        className="bg-surface border-border/60 focus:border-brand-primary/50 h-9 w-full rounded-lg border px-3 text-[13px] font-medium outline-none"
                      />
                    </div>
                    <div className="grid gap-3 md:grid-cols-2">
                      <ColorInput
                        label="Strip background"
                        value={customAnnBg}
                        onChange={setCustomAnnBg}
                      />
                      <ColorInput
                        label="Strip text"
                        value={customAnnFg}
                        onChange={setCustomAnnFg}
                      />
                    </div>
                    <div
                      className="mt-2 rounded-md px-3 py-2 text-center text-[12px] font-bold"
                      style={{ background: customAnnBg, color: customAnnFg }}
                    >
                      {customAnnText || '(preview of your announcement)'}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Save bar */}
        <div className="border-border/60 bg-surface flex items-center justify-between rounded-2xl border px-5 py-4 shadow-sm">
          <p className="text-ink-muted text-[12px]">
            Selected preset:{' '}
            <strong className="text-ink">
              {presetId === 'custom' ? 'Custom' : (FESTIVAL_THEMES[presetId]?.name ?? 'None')}
            </strong>{' '}
            — Active:{' '}
            <strong className={active ? 'text-emerald-600' : 'text-ink-muted'}>
              {active ? 'YES' : 'NO'}
            </strong>
          </p>
          <Button loading={upsert.isPending} onClick={() => upsert.mutate()}>
            <Save className="mr-1.5 h-4 w-4" /> Save & Apply
          </Button>
        </div>
      </div>

      {/* RIGHT — live storefront iframe; auto-reloads with applied theme. */}
      <div className="hidden xl:block">
        <LivePreviewPanel />
      </div>
    </div>
  );
}
